import React, { useState, useEffect } from 'react'
import { T, Spinner, Avatar, probColor, playColor, TeamTag } from '../ui'
import { teamNick } from '../plain'
import { getNFLMatchupSheets } from '../api'

// Matchup Sheets — the fast "where are the soft spots, and who's facing them" board.
// Toggle TDs / Yards; each position section lists the softest defenses (rank 1 = allows
// the most) and, under each, the players facing that defense this week with the model's
// read (anytime-TD % + book price, or projected yards vs the line). Research only.

const _ord = n => { const s = { 1: 'st', 2: 'nd', 3: 'rd' }[n < 20 ? n : n % 10] || 'th'; return `${n}${s}` }
const rankCol = r => (r == null ? T.muted : r <= 3 ? T.green : r <= 8 ? T.amber : T.muted)
const fmtOdds = o => (o == null ? '' : o > 0 ? `+${o}` : `${o}`)

function PlayerRow({ p, yards, onPlayer }) {
  return (
    <button onClick={() => onPlayer && onPlayer({ gsis_id: p.gsis_id, full_name: p.name, team: p.team, pos: p.pos, prop_type: p.prop_type })}
      style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 9, textAlign: 'left', cursor: 'pointer',
        background: 'transparent', border: 'none', padding: '7px 2px', color: T.text }}>
      <Avatar url={p.headshot} name={p.name} team={p.team} size={26} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {p.name} <span style={{ fontSize: 10, color: T.muted, fontWeight: 700 }}>{teamNick(p.team)}</span></div>
        {yards
          ? <div style={{ fontSize: 10.5, color: T.muted, marginTop: 1, fontFamily: T.mono }}>
              proj <b style={{ color: T.text }}>{p.proj ?? '—'}</b> vs {p.line ?? '—'}
              {p.line_source === 'projected' && <span style={{ color: T.faint }}> (proj)</span>}</div>
          : <div style={{ fontSize: 10.5, color: T.muted, marginTop: 1, fontFamily: T.mono }}>
              {p.line != null && <b style={{ color: T.text }}>o{p.line} </b>}
              {p.book_odds != null ? `${p.line != null ? '· ' : ''}${fmtOdds(p.book_odds)} · book ${p.book_prob ?? '—'}%`
                : (p.line != null ? '· no book line yet' : 'no book line yet')}</div>}
      </div>
      <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 15, color: playColor((p.prob || 0) / 100, p), minWidth: 44, textAlign: 'right' }}>
        {p.prob}%</div>
    </button>
  )
}

function DefenseCard({ d, yards, onPlayer }) {
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: '10px 12px', marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 7, borderBottom: `1px solid ${T.border}`, marginBottom: 4 }}>
        <span style={{ fontSize: 15, fontWeight: 900 }}><TeamTag abbr={d.defense} size={20} /></span>
        <span style={{ fontSize: 11, fontWeight: 900, color: rankCol(d.rank) }}>#{d.rank} softest</span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: T.muted, fontFamily: T.mono }}>
          {d.allowed != null ? `${d.allowed} ${d.unit}` : ''}</span>
      </div>
      {(d.facing || []).map((p, i) => <PlayerRow key={i} p={p} yards={yards} onPlayer={onPlayer} />)}
    </div>
  )
}

export default function MatchupSheetsTab({ season = 2026, week = 1, onPlayer }) {
  const [d, setD] = useState(null)
  const [mode, setMode] = useState('td')     // 'td' | 'yards'
  const [pos, setPos] = useState('all')      // 'all' | 'QB' | 'RB' | 'WR' | 'TE'
  useEffect(() => { let a = true; setD(null)
    getNFLMatchupSheets(season, week).then(r => { if (a) setD(r) }).catch(() => { if (a) setD({ td: [], yards: [] }) })
    return () => { a = false } }, [season, week])
  if (!d) return <div style={{ padding: 20 }}><Spinner /></div>
  const allGroups = (mode === 'td' ? d.td : d.yards) || []
  const posList = allGroups.map(g => g.pos)                 // positions available in this mode
  const groups = pos === 'all' ? allGroups : allGroups.filter(g => g.pos === pos)
  const Btn = ({ k, label }) => (
    <button onClick={() => { setMode(k); setPos('all') }} style={{ flex: 1, padding: '9px 0', cursor: 'pointer', borderRadius: 9,
      fontSize: 13, fontWeight: 800, border: `1px solid ${mode === k ? T.green : T.border}`,
      background: mode === k ? 'rgba(0,217,126,0.12)' : T.card, color: mode === k ? T.green : T.muted }}>{label}</button>
  )
  const PosChip = ({ k, label }) => (
    <button onClick={() => setPos(k)} style={{ padding: '6px 14px', cursor: 'pointer', borderRadius: 999, flex: 'none',
      fontSize: 12.5, fontWeight: 800, border: `1px solid ${pos === k ? T.green : T.border}`,
      background: pos === k ? 'rgba(0,217,126,0.12)' : T.card, color: pos === k ? T.green : T.muted }}>{label}</button>
  )
  return (
    <div style={{ padding: '12px 14px 90px' }}>
      <div style={{ fontSize: 12, color: T.muted, marginBottom: 10, lineHeight: 1.4 }}>
        The softest defenses this week and who’s facing them — <b style={{ color: T.text }}>rank 1 = allows the most</b>.
        Model = our shown read; research only.</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <Btn k="td" label="🎯 Touchdowns" /><Btn k="yards" label="📏 Yards" />
      </div>
      <div style={{ display: 'flex', gap: 7, marginBottom: 14, overflowX: 'auto', paddingBottom: 2 }}>
        <PosChip k="all" label="All" />
        {posList.map(p => <PosChip key={p} k={p} label={p} />)}
      </div>
      {groups.length === 0 && <div style={{ fontSize: 12, color: T.muted, padding: '20px 0', textAlign: 'center' }}>
        Sheets fill in once the board and lines post for this week.</div>}
      {groups.map((g, gi) => (
        <div key={gi} style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 900, color: T.green, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 9 }}>
            {g.label}</div>
          {(g.sections || []).length === 0
            ? <div style={{ fontSize: 11.5, color: T.muted }}>No soft spots with a play this week.</div>
            : g.sections.map((s, si) => <DefenseCard key={si} d={s} yards={mode === 'yards'} onPlayer={onPlayer} />)}
        </div>
      ))}
    </div>
  )
}
